<template>
  <div class="scripture-container">

    <!-- HERO -->
    <section class="sc-hero">
      <div class="sc-hero__stars" aria-hidden="true">
        <span v-for="n in 60" :key="n" class="star" :style="starStyle(n)"></span>
      </div>
      <div class="sc-hero__inner">
        <div class="sc-hero__eyebrow">
          <span class="eyebrow-rule"></span>
          Sacred Research &amp; Scriptural Inquiry
          <span class="eyebrow-rule"></span>
        </div>
        <h1 class="sc-hero__title">
          Scripture, Science<br />&amp; <em>Higher Dimensions</em>
        </h1>
        <p class="sc-hero__sub">
          Exploring the physics hidden within the canon — from the Book of Enoch
          to the Revelation of Yeshua — and why Yahweh is the only rational
          conclusion.
        </p>
        <div class="sc-hero__scroll-cue" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>
    </section>

    <!-- WHY I BELIEVE -->
    <section class="sc-belief">
      <div class="sc-inner">
        <div class="sc-belief__left">
          <div class="section-eyebrow">Personal Conviction</div>
          <h2 class="section-title">Why I Believe in<br />Yahweh</h2>
          <p class="sc-belief__body">
            My research career has been built on following evidence wherever it
            leads — through corporate structures, economic systems, and the hidden
            architectures of power. That same methodology, applied honestly to the
            cosmos, leads me to one conclusion: the universe is not an accident, and
            its Author has left a record.
          </p>
          <p class="sc-belief__body">
            The Scriptures are not mythology. They are a physics textbook written in
            the language of covenant. The closer I look at the biblical cosmology —
            layered heavens, dimensional beings, non-linear time — the more it
            matches what modern physics is only beginning to describe.
          </p>
          <p class="sc-belief__body">
            Yahweh is not the product of ancient superstition. He is the ground of
            being itself. And Yeshua is the proof-of-concept: a higher-dimensional
            being entering our 3D realm and demonstrating that the veil between
            worlds is thinner than we are told.
          </p>
        </div>
        <div class="sc-belief__right">
          <div class="sc-belief__pillars">
            <div v-for="(pillar, i) in beliefPillars" :key="i" class="belief-pillar">
              <div class="belief-pillar__num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="belief-pillar__title">{{ pillar.title }}</div>
                <div class="belief-pillar__text">{{ pillar.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SCRIPTURE AS PHYSICS -->
    <section class="sc-dimensions">
      <div class="sc-inner">
        <div class="section-eyebrow center">Cosmological Alignment</div>
        <h2 class="section-title center">Scripture as a Book of Physics</h2>
        <p class="sc-dimensions__intro">
          Every passage below is drawn from the KJV canon. Each one describes
          spatial, temporal, or ontological phenomena that map cleanly onto the
          language of modern physics — multi-dimensionality, non-Euclidean space,
          and beings that operate outside our spacetime.
        </p>

        <div class="dim-grid">
          <div v-for="(card, i) in dimensionCards" :key="i" class="dim-card">
            <div class="dim-card__glyph">{{ card.glyph }}</div>
            <div class="dim-card__ref">{{ card.ref }}</div>
            <blockquote class="dim-card__quote">"{{ card.kjv }}"</blockquote>
            <div class="dim-card__label">Physics interpretation</div>
            <p class="dim-card__interp">{{ card.interpretation }}</p>
            <div v-if="card.enoch" class="dim-card__enoch-wrap">
              <div class="dim-card__enoch-label">Enoch Parallel</div>
              <div class="dim-card__enoch">{{ card.enoch }}</div>
            </div>
          </div>
        </div>

        <!-- Alignment Table -->
        <div class="dim-table-wrap">
          <div class="dim-table-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
            Canon ↔ Enoch Alignment Matrix
          </div>
          <div class="dim-table">
            <div class="dim-table__head">
              <span>Biblical Passage</span>
              <span>Enoch Parallel</span>
              <span>Dimensional Interpretation</span>
            </div>
            <div v-for="(row, i) in alignmentTable" :key="i" class="dim-table__row">
              <div class="dim-table__cell">
                <span class="tc-ref">{{ row.passage }}</span>
                <span class="tc-desc">{{ row.passageDesc }}</span>
              </div>
              <div class="dim-table__cell">
                <span class="tc-ref tc-ref--enoch">{{ row.enoch }}</span>
                <span class="tc-desc">{{ row.enochDesc }}</span>
              </div>
              <div class="dim-table__cell">
                <span class="tc-interp">{{ row.interp }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- DIMENSIONAL EVENTS -->
    <section class="sc-events">
      <div class="sc-inner">
        <div class="section-eyebrow center">Documented Inter-Dimensional Events</div>
        <h2 class="section-title center">When the Veil Opened</h2>
        <div class="events-grid">
          <div v-for="(ev, i) in dimensionalEvents" :key="i" class="event-card">
            <div class="event-card__num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="event-card__icon">{{ ev.icon }}</div>
            <h3 class="event-card__title">{{ ev.title }}</h3>
            <div class="event-card__ref">{{ ev.ref }}</div>
            <p class="event-card__body">{{ ev.body }}</p>
            <div class="event-card__physics">
              <div class="event-physics-label">Physics Model</div>
              {{ ev.physics }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- DYNAMIC SCRIPTURE POSTS -->
    <section class="sc-posts">
      <div class="sc-inner">
        <div class="section-eyebrow center">Living Research</div>
        <h2 class="section-title center">Scripture &amp; Study</h2>
        <p class="sc-posts__intro">
          Ongoing research and reflection at the intersection of Scripture,
          cosmology, and the nature of Yahweh.
        </p>

        <div v-if="postsLoading" class="sc-loading">
          <div class="sc-spinner"></div>
          <p>Loading scripture posts...</p>
        </div>

        <div v-else-if="posts.length === 0" class="sc-empty">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          <h3>More to come</h3>
          <p>New scriptural research will appear here</p>
        </div>

        <div v-else class="sc-posts__grid">
          <div
            v-for="(post, i) in posts"
            :key="post.id"
            class="sc-post-card"
            @click="openPost(post)"
          >
            <div v-if="post.imageUrl" class="sc-post-card__img">
              <img :src="post.imageUrl" :alt="post.title" />
            </div>
            <div class="sc-post-card__body">
              <div class="sc-post-card__meta">
                <span class="sc-post-card__date">{{ formatDate(post.createdAt) }}</span>
                <span v-if="post.category" class="sc-post-card__cat">{{ post.category }}</span>
              </div>
              <h3 class="sc-post-card__title">{{ post.title }}</h3>
              <p class="sc-post-card__excerpt">{{ post.excerpt || truncate(post.content, 140) }}</p>
              <span class="sc-post-card__read">
                Read Study
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- POST DETAIL MODAL -->
    <transition name="modal-fade">
      <div v-if="selectedPost" class="sc-modal-backdrop" @click.self="selectedPost = null">
        <div class="sc-modal">
          <button class="sc-modal__close" @click="selectedPost = null" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div v-if="selectedPost.imageUrl" class="sc-modal__hero">
            <img :src="selectedPost.imageUrl" :alt="selectedPost.title" />
          </div>
          <div class="sc-modal__content">
            <div class="sc-modal__meta">
              <span class="sc-post-card__date">{{ formatDate(selectedPost.createdAt) }}</span>
              <span v-if="selectedPost.category" class="sc-post-card__cat">{{ selectedPost.category }}</span>
            </div>
            <h2 class="sc-modal__title">{{ selectedPost.title }}</h2>
            <p v-if="selectedPost.excerpt" class="sc-modal__excerpt">{{ selectedPost.excerpt }}</p>
            <div class="sc-modal__body" v-html="renderMarkdown(selectedPost.content)"></div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { db } from '../firebase'

export default {
  name: 'ScriptureView',
  data () {
    return {
      postsLoading: false,
      posts: [],
      selectedPost: null,

      beliefPillars: [
        {
          title: 'The cosmos demands a Lawgiver',
          text: 'Fine-tuning, the anthropic constants, the arrow of time — none of these resolve without a mind outside the system.'
        },
        {
          title: 'Scripture predicts what physics discovers',
          text: "Paul's four-dimensional language in Ephesians 3:18 and Enoch's seven heavens predate string theory by two millennia."
        },
        {
          title: 'Yeshua is the dimensional proof',
          text: 'A resurrection body that moves through walls yet eats fish is the behavior of a being operating in more spatial dimensions than we inhabit.'
        },
        {
          title: 'The Book of Enoch fills the gaps',
          text: "The canon and the Enoch tradition, taken together, describe a cosmology of layered realms, imprisoned Watchers, and angelic hierarchies that reads like a physics manual."
        }
      ],

      dimensionCards: [
        {
          glyph: '⚔',
          ref: '1 Chronicles 21:16 (KJV)',
          kjv: 'And David lifted up his eyes, and saw the angel of the LORD stand between the earth and the heaven, having a drawn sword in his hand.',
          interpretation: 'A being simultaneously occupying the boundary between two realms — the behavior of a higher-dimensional object intersecting our 3D space at a liminal surface.',
          enoch: '1 Enoch 39:12–13 — Angels positioned across multiple heavens, neither fully in one realm nor another.'
        },
        {
          glyph: '🪜',
          ref: 'Genesis 28:12 (KJV)',
          kjv: 'And he dreamed, and behold a ladder set up on the earth, and the top of it reached to heaven: and behold the angels of God ascending and descending on it.',
          interpretation: 'A dimensional gateway — a fixed corridor through which beings transit between stacked realms. Jacob saw the infrastructure of a layered cosmos.',
          enoch: '1 Enoch 17–19 — Enoch is shown the gates of heaven and the portals through which the heavenly hosts pass.'
        },
        {
          glyph: '☁',
          ref: '2 Corinthians 12:2 (KJV)',
          kjv: 'I knew a man in Christ above fourteen years ago, whether in the body, I cannot tell; or whether out of the body, I cannot tell: God knoweth; such an one caught up to the third heaven.',
          interpretation: "Paul's language implies at minimum three discrete heavens — a stacked cosmological architecture consistent with extra-dimensional models. His uncertainty about his body state implies the journey transcended physical constraints.",
          enoch: "1 Enoch 14–36 — The Book of Watchers describes Enoch's tour through seven distinct heavens, each with its own physics and inhabitants."
        },
        {
          glyph: '🔥',
          ref: 'Ezekiel 1:15–21 (KJV)',
          kjv: 'Now as I beheld the living creatures, behold one wheel upon the earth by the living creatures... The appearance of the wheels and their work was like unto the colour of a beryl… and their rings were full of eyes round about them four.',
          interpretation: 'Wheels within wheels, omnidirectional movement, eyes covering every surface — this is the geometry of a non-Euclidean object: a being whose symmetry group exceeds anything in three dimensions.',
          enoch: '1 Enoch 71:7 — The heavenly realm described as a structure of fire and crystal that defies normal spatial reasoning.'
        },
        {
          glyph: '📐',
          ref: 'Ephesians 3:18 (KJV)',
          kjv: 'May be able to comprehend with all saints what is the breadth, and length, and depth, and height.',
          interpretation: 'Paul lists four spatial dimensions — breadth, length, depth, and height — in a context describing the love of Christ. This is a four-dimensional coordinate system predating physics by 1,900 years.',
          enoch: null
        },
        {
          glyph: '🏙',
          ref: 'Revelation 21:16 (KJV)',
          kjv: 'And the city lieth foursquare, and the length is as large as the breadth: and he measured the city with the reed, twelve thousand furlongs. The length and the breadth and the height of it are equal.',
          interpretation: 'A perfect cube — or tesseract — whose length, breadth, and height are equal. In four-dimensional geometry, this describes a hypercube: the New Jerusalem may be a 4D structure manifesting into 3D reality.',
          enoch: null
        },
        {
          glyph: '⏳',
          ref: 'Psalm 90:4 (KJV)',
          kjv: 'For a thousand years in thy sight are but as yesterday when it is past, and as a watch in the night.',
          interpretation: 'Yahweh exists in a temporal dimension orthogonal to ours. One thousand of our years is one of His days — the mathematics of a being operating outside linear time, consistent with a Creator who precedes spacetime itself.',
          enoch: null
        }
      ],

      alignmentTable: [
        {
          passage: '1 Chronicles 21:16',
          passageDesc: 'Angel between heaven & earth',
          enoch: '1 Enoch 39:12–13',
          enochDesc: 'Angels stationed across multiple realms',
          interp: 'Higher-dimensional beings intersecting 3D space'
        },
        {
          passage: 'Genesis 28:12',
          passageDesc: "Jacob's Ladder",
          enoch: '1 Enoch 17–19',
          enochDesc: 'Portals and heavenly gates',
          interp: 'Dimensional gateways between stacked realms'
        },
        {
          passage: '2 Corinthians 12:2–4',
          passageDesc: "Paul's Third Heaven",
          enoch: '1 Enoch 14–36',
          enochDesc: 'Seven distinct heavens with unique physics',
          interp: 'Layered multi-dimensional cosmology'
        },
        {
          passage: 'Ezekiel 1:15–21',
          passageDesc: 'Wheels within wheels, eyes on rings',
          enoch: '1 Enoch 71:7',
          enochDesc: 'Fiery, crystalline non-Euclidean realms',
          interp: 'Non-Euclidean geometry; beings with extra-dimensional symmetry'
        },
        {
          passage: 'Ephesians 3:18',
          passageDesc: 'Breadth, length, depth, and height',
          enoch: '—',
          enochDesc: 'Four explicit spatial dimensions',
          interp: 'A four-dimensional coordinate system predating modern physics'
        },
        {
          passage: 'Psalm 90:4',
          passageDesc: 'A thousand years as a day',
          enoch: '—',
          enochDesc: 'God outside linear time',
          interp: 'The Creator exists in a temporal dimension orthogonal to ours'
        }
      ],

      dimensionalEvents: [
        {
          icon: '🕊',
          title: 'Yeshua Descends into the Prison Realm',
          ref: '1 Peter 3:18–20 (KJV)',
          body: 'Being put to death in the flesh, but quickened by the Spirit: By which also he went and preached unto the spirits in prison; which sometime were disobedient, when once the longsuffering of God waited in the days of Noah. After His death, Yeshua descended into a sub-realm — the dimensional prison holding the disobedient spirits of the Noahic age — consistent with 1 Enoch 21\'s description of the abyss where fallen Watchers are confined.',
          physics: 'Transit from physical realm to spiritual realm to prison sub-realm. Three distinct dimensions traversed in sequence.'
        },
        {
          icon: '👁',
          title: "Elisha's Servant Sees the Hidden Army",
          ref: '2 Kings 6:17 (KJV)',
          body: 'And Elisha prayed, and said, LORD, I pray thee, open his eyes, that he may see. And the LORD opened the eyes of the young man; and he saw: and, behold, the mountain was full of horses and chariots of fire round about Elisha. A vast angelic army occupied the same physical space as the visible world — invisible not because it was elsewhere, but because it existed in an adjacent dimensional layer inaccessible to unaugmented human perception.',
          physics: 'Two realms co-located in the same 3D space, separated by a dimensional barrier. Yahweh removes the perceptual filter temporarily.'
        },
        {
          icon: '👤',
          title: 'Samuel Crosses the Boundary of Death',
          ref: '1 Samuel 28:12–15 (KJV)',
          body: "And when the woman saw Samuel, she cried with a loud voice… And Samuel said to Saul, Why hast thou disquieted me, to bring me up? The medium's terror was genuine — she had not expected a real manifestation. Samuel, a conscious being in the post-death realm, temporarily crossed the dimensional boundary into physical space and demonstrated knowledge of future events, consistent with a non-linear temporal perspective.",
          physics: 'A conscious being from a holding realm projects into 3D reality. Future knowledge implies access to a time dimension not available to physical beings.'
        },
        {
          icon: '⚡',
          title: 'The Transfiguration of Yeshua',
          ref: 'Matthew 17:2 (KJV)',
          body: 'And was transfigured before them: and his face did shine as the sun, and his raiment was white as the light. And, behold, there appeared unto them Moses and Elias talking with him. In a single moment, Yeshua\'s physical appearance shifts to emit light at solar intensity while two beings from the post-death realm materialize and hold a conversation. This is a dimensional overlay: the higher realm momentarily bleeds through into the physical.',
          physics: 'A higher-dimensional being briefly ceasing to suppress its native luminosity. Simultaneous materialization of post-death realm inhabitants into 3D space.'
        }
      ]
    }
  },

  mounted () {
    this.loadPosts()
  },

  methods: {
    async loadPosts () {
      this.postsLoading = true
      try {
        const snap = await db.collection('scripture_posts')
          .where('status', '==', 'published')
          .orderBy('createdAt', 'desc')
          .get()
        this.posts = snap.docs.map(function (d) { return Object.assign({ id: d.id }, d.data()) })
      } catch (e) {
        console.error('Error loading scripture posts:', e)
      } finally {
        this.postsLoading = false
      }
    },

    openPost (post) {
      this.selectedPost = post
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    formatDate (ts) {
      if (!ts) return ''
      var d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },

    truncate (text, len) {
      if (!text) return ''
      var s = text.replace(/[#*>`\-]/g, '').trim()
      return s.length > len ? s.substring(0, len) + '...' : s
    },

    renderMarkdown (content) {
      if (!content) return ''
      var h = content
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>')
      return '<p>' + h + '</p>'
    },

    starStyle (n) {
      var seed = n * 137.508
      return {
        left: (seed % 100) + '%',
        top: ((seed * 0.618) % 100) + '%',
        width: (1 + (n % 3)) + 'px',
        height: (1 + (n % 3)) + 'px',
        animationDelay: (seed % 4) + 's',
        animationDuration: (3 + (n % 5)) + 's'
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,600&family=Inter:wght@400;500;600;700&family=Cinzel:wght@400;600;700&display=swap');

.scripture-container {
  min-height: 100vh;
  background: #0a0e1a;
  color: #f5f0e8;
  font-family: 'Inter', sans-serif;
}

.sc-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
}

/* ── Section shared ── */
.section-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #c9a84c;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.section-eyebrow.center { justify-content: center; }

.section-title {
  font-family: 'Cinzel', serif;
  font-size: clamp(26px, 3.5vw, 46px);
  font-weight: 600;
  color: #f5f0e8;
  line-height: 1.15;
  margin: 0 0 32px;
  letter-spacing: 0.01em;
}
.section-title.center { text-align: center; }

/* ── Hero ── */
.sc-hero {
  position: relative;
  min-height: 92vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 100px 24px 60px;
  overflow: hidden;
  background: radial-gradient(ellipse at 50% 60%, #0e1c3a 0%, #060b14 70%);
}

.sc-hero__stars {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.star {
  position: absolute;
  background: #fff;
  border-radius: 50%;
  opacity: 0;
  animation: twinkle linear infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 0; }
  50%       { opacity: 0.7; }
}

.sc-hero__inner {
  position: relative;
  z-index: 2;
  max-width: 860px;
}

.sc-hero__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c9a84c;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 28px;
}

.eyebrow-rule {
  display: block;
  width: 40px;
  height: 1px;
  background: #c9a84c;
  opacity: 0.6;
}

.sc-hero__title {
  font-family: 'Cinzel', serif;
  font-size: clamp(36px, 6vw, 78px);
  font-weight: 700;
  color: #f5f0e8;
  line-height: 1.1;
  margin: 0 0 24px;
  letter-spacing: 0.02em;
}

.sc-hero__title em {
  font-family: 'EB Garamond', serif;
  font-style: italic;
  color: #c9a84c;
}

.sc-hero__sub {
  font-family: 'EB Garamond', serif;
  font-size: clamp(17px, 2vw, 22px);
  line-height: 1.7;
  color: rgba(245,240,232,0.75);
  max-width: 700px;
  margin: 0 auto;
}

.sc-hero__scroll-cue {
  margin-top: 56px;
  color: #c9a84c;
  opacity: 0.5;
  animation: bounce-y 2.5s ease-in-out infinite;
}

@keyframes bounce-y {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(10px); }
}

/* ── Belief ── */
.sc-belief {
  padding: 96px 0;
  background: #080e1c;
  border-top: 1px solid rgba(201,168,76,0.15);
  border-bottom: 1px solid rgba(201,168,76,0.15);
}

.sc-belief .sc-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
}

.sc-belief__body {
  font-family: 'EB Garamond', serif;
  font-size: 19px;
  line-height: 1.8;
  color: rgba(245,240,232,0.82);
  margin: 0 0 20px;
}

.sc-belief__pillars {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(201,168,76,0.15);
}

.belief-pillar {
  display: flex;
  gap: 20px;
  padding: 22px 24px;
  border-bottom: 1px solid rgba(201,168,76,0.08);
  transition: background 0.2s;
}
.belief-pillar:last-child { border-bottom: none; }
.belief-pillar:hover { background: rgba(201,168,76,0.05); }

.belief-pillar__num {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 600;
  color: #8a6e2a;
  letter-spacing: 0.1em;
  padding-top: 3px;
  flex-shrink: 0;
  width: 26px;
}

.belief-pillar__title {
  font-family: 'EB Garamond', serif;
  font-size: 17px;
  font-weight: 600;
  color: #f5f0e8;
  margin-bottom: 6px;
}

.belief-pillar__text {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.65;
  color: #718096;
  font-weight: 300;
}

/* ── Dimensions ── */
.sc-dimensions {
  padding: 96px 0;
  background: #0a0e1a;
}

.sc-dimensions__intro {
  font-family: 'EB Garamond', serif;
  font-size: 19px;
  line-height: 1.75;
  color: rgba(245,240,232,0.7);
  max-width: 780px;
  margin: 0 auto 64px;
  text-align: center;
}

.dim-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2px;
  margin-bottom: 64px;
}

.dim-card {
  background: #0d1827;
  border: 1px solid rgba(201,168,76,0.12);
  padding: 32px;
  transition: border-color 0.25s;
}
.dim-card:hover { border-color: rgba(201,168,76,0.4); }

.dim-card__glyph {
  font-size: 28px;
  margin-bottom: 16px;
  display: block;
}

.dim-card__ref {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: #c9a84c;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.dim-card__quote {
  font-family: 'EB Garamond', serif;
  font-style: italic;
  font-size: 17px;
  line-height: 1.7;
  color: #f5f0e8;
  border-left: 2px solid #c9a84c;
  padding-left: 16px;
  margin: 0 0 20px;
}

.dim-card__label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8a6e2a;
  margin-bottom: 8px;
}

.dim-card__interp {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.7;
  color: #718096;
  margin: 0 0 16px;
}

.dim-card__enoch-wrap {
  background: rgba(201,168,76,0.04);
  border: 1px solid rgba(201,168,76,0.12);
  border-left: 2px solid #8a6e2a;
  padding: 12px 14px;
  margin-top: 12px;
}

.dim-card__enoch-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #8a6e2a;
  margin-bottom: 5px;
}

.dim-card__enoch {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: rgba(201,168,76,0.7);
  line-height: 1.55;
}

/* Alignment Table */
.dim-table-wrap {
  border: 1px solid rgba(201,168,76,0.15);
  overflow: hidden;
}

.dim-table-header {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(201,168,76,0.05);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  padding: 16px 24px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #c9a84c;
}

.dim-table { width: 100%; }

.dim-table__head {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  background: rgba(0,0,0,0.3);
  border-bottom: 1px solid rgba(201,168,76,0.1);
}

.dim-table__head span {
  padding: 12px 20px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8a6e2a;
}

.dim-table__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  border-bottom: 1px solid rgba(201,168,76,0.05);
  transition: background 0.15s;
}
.dim-table__row:last-child { border-bottom: none; }
.dim-table__row:hover { background: rgba(201,168,76,0.04); }

.dim-table__cell {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-right: 1px solid rgba(201,168,76,0.05);
}
.dim-table__cell:last-child { border-right: none; }

.tc-ref {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 600;
  color: #c9a84c;
  letter-spacing: 0.04em;
}

.tc-ref--enoch { color: rgba(201,168,76,0.55); }

.tc-desc {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #718096;
  line-height: 1.5;
}

.tc-interp {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(245,240,232,0.75);
}

/* ── Events ── */
.sc-events {
  padding: 96px 0;
  background: #080e1c;
  border-top: 1px solid rgba(201,168,76,0.12);
  border-bottom: 1px solid rgba(201,168,76,0.12);
}

.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2px;
  margin-top: 8px;
}

.event-card {
  background: #0a0e1a;
  border: 1px solid rgba(201,168,76,0.1);
  padding: 32px;
  position: relative;
  transition: border-color 0.25s, background 0.25s;
}
.event-card:hover { border-color: rgba(201,168,76,0.4); background: #0d1827; }

.event-card__num {
  position: absolute;
  top: 20px;
  right: 20px;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(201,168,76,0.18);
  letter-spacing: 0.05em;
}

.event-card__icon { font-size: 32px; margin-bottom: 18px; }

.event-card__title {
  font-family: 'EB Garamond', serif;
  font-size: 20px;
  font-weight: 600;
  color: #f5f0e8;
  line-height: 1.3;
  margin: 0 0 8px;
}

.event-card__ref {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: #c9a84c;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.event-card__body {
  font-family: 'EB Garamond', serif;
  font-size: 16px;
  line-height: 1.75;
  color: rgba(245,240,232,0.72);
  margin: 0 0 18px;
}

.event-card__physics {
  background: rgba(201,168,76,0.06);
  border: 1px solid rgba(201,168,76,0.18);
  border-left: 3px solid #c9a84c;
  padding: 14px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.65;
  color: rgba(201,168,76,0.85);
}

.event-physics-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8a6e2a;
  margin-bottom: 6px;
}

/* ── Posts ── */
.sc-posts {
  padding: 96px 0;
  background: #0a0e1a;
}

.sc-posts__intro {
  font-family: 'EB Garamond', serif;
  font-size: 18px;
  line-height: 1.7;
  color: #718096;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 56px;
}

.sc-loading, .sc-empty {
  text-align: center;
  padding: 80px 20px;
}

.sc-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 16px;
  border: 3px solid rgba(201,168,76,0.1);
  border-top-color: #c9a84c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.sc-loading p, .sc-empty p { font-size: 14px; color: #718096; }
.sc-empty svg { margin: 0 auto 20px; color: #1f2937; display: block; }
.sc-empty h3 {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  color: rgba(245,240,232,0.6);
  margin-bottom: 8px;
}

.sc-posts__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2px;
}

.sc-post-card {
  background: #0d1827;
  border: 1px solid rgba(201,168,76,0.1);
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.25s, transform 0.25s;
}
.sc-post-card:hover { border-color: rgba(201,168,76,0.4); transform: translateY(-4px); }

.sc-post-card__img { height: 200px; overflow: hidden; background: #060b14; position: relative; }
.sc-post-card__img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
.sc-post-card:hover .sc-post-card__img img { transform: scale(1.05); }

.sc-post-card__body { padding: 24px; }

.sc-post-card__meta { display: flex; gap: 10px; align-items: center; margin-bottom: 12px; }

.sc-post-card__date {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
}

.sc-post-card__cat {
  font-family: 'Cinzel', serif;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #c9a84c;
  background: rgba(201,168,76,0.08);
  border: 1px solid rgba(201,168,76,0.2);
  padding: 3px 10px;
}

.sc-post-card__title {
  font-family: 'EB Garamond', serif;
  font-size: 20px;
  font-weight: 600;
  color: #f5f0e8;
  line-height: 1.3;
  margin-bottom: 10px;
}

.sc-post-card__excerpt {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.7;
  color: #718096;
  margin-bottom: 18px;
}

.sc-post-card__read {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9a84c;
  transition: gap 0.2s;
}
.sc-post-card:hover .sc-post-card__read { gap: 10px; }

/* ── Modal ── */
.sc-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(6,11,20,0.9);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 48px 16px;
}

.sc-modal {
  background: #0d1827;
  border: 1px solid rgba(201,168,76,0.15);
  max-width: 780px;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.sc-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: rgba(0,0,0,0.6);
  border: 1px solid rgba(201,168,76,0.15);
  color: #f5f0e8;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.sc-modal__close:hover { background: rgba(201,168,76,0.2); }

.sc-modal__hero { width: 100%; height: 320px; overflow: hidden; }
.sc-modal__hero img { width: 100%; height: 100%; object-fit: cover; }

.sc-modal__content { padding: 40px 48px; }

.sc-modal__meta { display: flex; gap: 12px; margin-bottom: 16px; }

.sc-modal__title {
  font-family: 'Cinzel', serif;
  font-size: clamp(22px, 3vw, 34px);
  font-weight: 700;
  color: #f5f0e8;
  line-height: 1.2;
  margin-bottom: 16px;
}

.sc-modal__excerpt {
  font-family: 'EB Garamond', serif;
  font-size: 20px;
  font-style: italic;
  color: #8a6e2a;
  margin-bottom: 28px;
  line-height: 1.6;
}

.sc-modal__body {
  font-family: 'EB Garamond', serif;
  font-size: 19px;
  line-height: 1.85;
  color: rgba(245,240,232,0.82);
}

/* Modal body rendered HTML — no >>> needed, use plain classes instead */
.sc-modal__body h1 { font-family: 'Cinzel', serif; font-size: 28px; color: #f5f0e8; margin: 40px 0 16px; }
.sc-modal__body h2 { font-family: 'Cinzel', serif; font-size: 22px; color: #f5f0e8; margin: 32px 0 14px; }
.sc-modal__body h3 { font-family: 'Cinzel', serif; font-size: 18px; color: #c9a84c; margin: 24px 0 12px; }
.sc-modal__body p  { margin-bottom: 20px; }
.sc-modal__body strong { font-weight: 600; color: #f5f0e8; }
.sc-modal__body em { font-style: italic; color: #c9a84c; }
.sc-modal__body blockquote {
  border-left: 3px solid #c9a84c;
  padding: 16px 22px;
  background: rgba(201,168,76,0.05);
  margin: 24px 0;
  font-style: italic;
  color: rgba(245,240,232,0.8);
}

/* Modal transition */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .sc-belief .sc-inner { grid-template-columns: 1fr; gap: 48px; }
  .dim-grid, .sc-posts__grid, .events-grid { grid-template-columns: 1fr; }
  .dim-table__head, .dim-table__row { grid-template-columns: 1fr; }
  .dim-table__head span:not(:first-child) { display: none; }
  .sc-modal__content { padding: 28px 24px; }
}
</style>